import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { PublicHeader } from '../components/PublicHeader';
import { SiteFooter } from '../components/SiteFooter';
import {
  SaleCard, humanise, landSize, salePhotoUrl, verifiedLine, type PublicSale,
} from '../components/SaleCard';
import { ListingMedia, type MediaUrls } from '../components/ListingMedia';
import { ReportListing } from '../components/ReportListing';
import { posterLabel } from '../components/ListingCard';
import { useApi } from '../lib/useApi';
import { VERIFIED_LIMIT } from '../lib/saleverification';
import { recordSaleView } from '../lib/visitor';
import { usePortal } from '../lib/portal';
import { apiRequest, ApiError } from '../lib/api';
import { money } from '../lib/format';
import {
  Alert, Button, ErrorBlock, Field, LoadingBlock, Select, TextInput,
} from '../components/ui';
import type { VideoChapter } from '../components/Video360';
import '../styles/public.css';
import '../styles/discover.css';

/**
 * One property for sale, in full.
 *
 * The facts list is longer than a rental's and that is the point of the page:
 * tenure, title status, land size and service charge are what a buyer is
 * actually deciding on, and a page that buries them under a description is a
 * page that generates phone calls asking for them.
 */

const SALE_MEDIA_URLS: MediaUrls = {
  photo: salePhotoUrl,
  panorama: salePhotoUrl,
  video: (id) => `/api/v1/sales/videos/${id}`,
};

interface Detail extends PublicSale {
  video_projection?: string | null;
  video_chapters?: VideoChapter[] | null;
  contact_preference: string;
  contact_name: string | null;
  /** Who posted it (156). */
  poster_role?: string | null;
  contact_phone: string | null;
  contact_email: string | null;
}
interface Response {
  listing: Detail;
  similar: PublicSale[];
  amenities: { code: string; label: string }[];
  propertyTypes: { code: string; label: string }[];
}

const TITLE_STATUS: Record<string, string> = {
  ready: 'Title deed ready',
  processing: 'Title being processed',
  share_certificate: 'Share certificate',
  unknown: 'Not stated',
};

/**
 * The enquiry form.
 *
 * Three fields more than the rental one, and each pays for itself: what the
 * buyer wants, when they could view, and how they would pay. A seller deciding
 * which of nine enquiries to ring first is deciding on exactly this.
 */
function SaleEnquiry({ listingId, contactName }: { listingId: string; contactName: string | null }) {
  const [form, setForm] = useState({
    name: '', phone: '', email: '', message: '',
    intent: 'general', preferredViewingDate: '', financing: '',
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true); setError(null);
    try {
      // Empty strings are omitted rather than sent: the server validates a date
      // and an enum, and "" is neither.
      const body: Record<string, unknown> = {
        name: form.name, intent: form.intent,
      };
      if (form.phone.trim()) body.phone = form.phone.trim();
      if (form.email.trim()) body.email = form.email.trim();
      if (form.message.trim()) body.message = form.message.trim();
      if (form.preferredViewingDate) body.preferredViewingDate = form.preferredViewingDate;
      if (form.financing) body.financing = form.financing;

      await apiRequest(`/api/v1/sales/${listingId}/enquiries`, { method: 'POST', body });
      setSent(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not send. Please try again.');
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="dsc-enquiry">
        <Alert tone="success">
          Sent. {contactName ?? 'The seller'} has your details and will be in touch.
        </Alert>
      </div>
    );
  }

  return (
    <form className="dsc-enquiry" onSubmit={submit}>
      <h2>Enquire about this property</h2>
      {error && <Alert tone="danger">{error}</Alert>}

      <Field label="Your name" htmlFor="s-name">
        <TextInput id="s-name" required value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })} />
      </Field>
      <Field label="Phone" htmlFor="s-phone" hint="A phone number or an email — either is enough.">
        <TextInput id="s-phone" inputMode="tel" value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })} />
      </Field>
      <Field label="Email" htmlFor="s-email">
        <TextInput id="s-email" type="email" value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })} />
      </Field>
      <Field label="What would you like to do?" htmlFor="s-intent">
        <Select id="s-intent" value={form.intent}
          onChange={(e) => setForm({ ...form, intent: e.target.value })}>
          <option value="general">Ask a question</option>
          <option value="viewing">Arrange a viewing</option>
          <option value="offer">Make an offer</option>
          <option value="finance">Ask about financing</option>
        </Select>
      </Field>
      {form.intent === 'viewing' && (
        <Field label="Preferred date" htmlFor="s-date">
          <TextInput id="s-date" type="date" value={form.preferredViewingDate}
            onChange={(e) => setForm({ ...form, preferredViewingDate: e.target.value })} />
        </Field>
      )}
      <Field label="How would you pay?" htmlFor="s-fin" hint="Optional, and it moves your enquiry up the list.">
        <Select id="s-fin" value={form.financing}
          onChange={(e) => setForm({ ...form, financing: e.target.value })}>
          <option value="">Rather not say</option>
          <option value="cash">Cash</option>
          <option value="mortgage">Mortgage</option>
          <option value="unsure">Not sure yet</option>
        </Select>
      </Field>
      <Field label="Message" htmlFor="s-msg">
        <textarea id="s-msg" className="input" rows={3}
          placeholder="Is the title ready? Could I see it this weekend?"
          value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
      </Field>
      <Button type="submit" block loading={sending}>Send enquiry</Button>
    </form>
  );
}

export function SaleDetail() {
  const { id } = useParams();
  const { siteHome } = usePortal();
  const view = useApi<Response>(id ? `/api/v1/sales/${id}` : null);

  /*
   * Counted once the advert has actually loaded, and keyed on the id that came
   * back rather than the one in the URL: a 404 or a withdrawn listing must not
   * count as somebody looking at a property. Above the early returns because a
   * hook cannot sit below one.
   */
  const loadedId = view.data?.listing.id ?? null;
  useEffect(() => { if (loadedId) recordSaleView(loadedId); }, [loadedId]);

  if (view.loading) {
    return <><PublicHeader /><div className="page"><LoadingBlock /></div><SiteFooter /></>;
  }
  if (view.error) {
    // A withdrawn advert, or one whose publication slot ran out, is the common
    // case here rather than a fault. Say so and offer the way back.
    return (
      <>
        <PublicHeader />
        <div className="page dsc-gone">
          {view.error.status === 404 ? (
            <>
              <h1>That property is no longer advertised</h1>
              <p className="page-sub">It may have been sold or withdrawn.</p>
              <Link className="btn btn-primary" to="/buy">Back to search</Link>
            </>
          ) : <ErrorBlock error={view.error} onRetry={view.reload} />}
        </div>
        <SiteFooter />
      </>
    );
  }

  const l = view.data!.listing;
  const labels = new Map(view.data!.amenities.map((a) => [a.code, a.label]));
  const where = [l.estate_area, l.county].filter(Boolean).join(', ');
  const land = landSize(l.land_size, l.land_size_unit);
  const gone = l.status === 'sold' || l.status === 'reserved';

  return (
    <>
      <PublicHeader />

      <div className="dsc-detail">
        <nav className="dsc-crumbs" aria-label="Breadcrumb">
          <Link to={siteHome}>Home</Link>
          <span aria-hidden> › </span>
          <Link to="/buy">Buy property</Link>
          <span aria-hidden> › </span>
          <span>{where || l.title}</span>
        </nav>

        <div className="dsc-detail-grid">
          <div>
            <ListingMedia
              title={l.title}
              photoIds={l.photo_ids ?? []}
              panoramas={l.panoramas ?? []}
              videoId={l.video_id ?? null}
              videoProjection={l.video_projection ?? null}
              videoChapters={l.video_chapters ?? null}
              urls={SALE_MEDIA_URLS}
            />

            {/* Above the title: somebody who arrived from a search result needs
                to learn this before they read the price and start planning. */}
            {gone && (
              <p className="dsc-taken" role="status">
                <strong>
                  {l.status === 'sold' ? 'This property has been sold.' : 'This property is under offer.'}
                </strong>{' '}
                It is no longer taking enquiries. Browse the other properties below.
              </p>
            )}

            <header className="dsc-head">
              <h1>{l.title}</h1>
              {where && <p className="dsc-where">{where}</p>}
              <p className="dsc-price">
                {money(l.asking_price)}
                {l.price_negotiable && <span className="dsc-deposit">Negotiable</span>}
              </p>

              {/*
                * THE CHECK, AND ITS EDGES, IN THE SAME BREATH.
                *
                * The specification forbids representing this as a guarantee of
                * title. The way that rule gets broken is not by writing
                * "guaranteed" — it is by printing a badge and letting the reader
                * decide what it covers. So the claim, its date and its limit sit
                * together, and the limit is not in smaller type than the claim.
                */}
              {l.verified_at && (
                <div className="dsc-checked">
                  <p className="dsc-checked-claim">
                    ✓ {verifiedLine(l.verified_capacity)}, on{' '}
                    {new Date(l.verified_at).toLocaleDateString('en-KE',
                      { day: 'numeric', month: 'long', year: 'numeric' })}.
                  </p>
                  {/* From shared, not typed out here. The claim and its limit
                      are one unit — the limit drifting away from the claim is
                      exactly how "we checked the seller" becomes heard as "we
                      checked the title". */}
                  <p className="dsc-checked-limit">{VERIFIED_LIMIT}</p>
                </div>
              )}
            </header>

            <dl className="dsc-facts">
              {l.property_type && <div><dt>Type</dt><dd>{humanise(l.property_type)}</dd></div>}
              {l.bedrooms !== null && <div><dt>Bedrooms</dt><dd>{l.bedrooms}</dd></div>}
              {l.bathrooms !== null && <div><dt>Bathrooms</dt><dd>{l.bathrooms}</dd></div>}
              {land && <div><dt>Land size</dt><dd>{land}</dd></div>}
              {l.floor_area_sqm && (
                <div><dt>Floor area</dt><dd>{Number(l.floor_area_sqm).toLocaleString('en-KE')} m²</dd></div>
              )}
              <div>
                <dt>Tenure</dt>
                <dd>
                  {humanise(l.tenure)}
                  {/* A leasehold with 12 years left and one with 92 are
                      different properties, so the number travels with the word. */}
                  {l.tenure === 'leasehold' && l.lease_years_remaining !== null
                    && ` · ${l.lease_years_remaining} years left`}
                </dd>
              </div>
              <div>
                <dt>Title</dt>
                <dd>
                  {TITLE_STATUS[l.title_deed_status] ?? humanise(l.title_deed_status)}
                  {/*
                    * WHOSE STATEMENT THIS IS.
                    *
                    * The line is typed by the seller into their own form. Printed
                    * bare it reads as something NyumbaSuite established, and a
                    * buyer who loses money on that reading was misled by this
                    * page. Attributed, it is exactly what it is — and when a
                    * check HAS happened, the badge above says so with its date.
                    */}
                  <span className="muted"> — the seller’s own statement</span>
                </dd>
              </div>
              {l.year_built !== null && <div><dt>Built</dt><dd>{l.year_built}</dd></div>}
              {l.service_charge_monthly && (
                <div><dt>Service charge</dt><dd>{money(l.service_charge_monthly)} a month</dd></div>
              )}
              {l.mortgage_accepted !== null && (
                <div><dt>Mortgage</dt><dd>{l.mortgage_accepted ? 'Accepted' : 'Cash only'}</dd></div>
              )}
            </dl>

            {l.description && (
              <section className="dsc-section">
                <h2>About this property</h2>
                <p className="dsc-desc">{l.description}</p>
              </section>
            )}

            {l.amenities.length > 0 && (
              <section className="dsc-section">
                <h2>Features</h2>
                <ul className="dsc-amenities">
                  {l.amenities.map((a) => (
                    <li key={a}><span aria-hidden>✓</span> {labels.get(a) ?? humanise(a)}</li>
                  ))}
                </ul>
              </section>
            )}
          </div>

          <aside className="dsc-side">
            {/* A sold property publishes no way to be contacted about it. The
                server refuses the enquiry either way; hiding the form is what
                stops the wasted call being made at all. */}
            {gone ? (
              <div className="dsc-contact">
                <h2>Enquiries closed</h2>
                <p className="muted">
                  {l.status === 'sold'
                    ? 'This property has been sold, so it is not taking enquiries.'
                    : 'This property is under offer, so it is not taking enquiries.'}
                </p>
                <Link to="/buy" className="btn btn-secondary" style={{ marginTop: 12 }}>
                  See properties still for sale
                </Link>
              </div>
            ) : (
              <>
                <div className="dsc-contact">
                  <h2>Contact</h2>
                  <p className="dsc-contact-name">{l.contact_name ?? 'The seller'}</p>
                  {posterLabel(l.poster_role) && <p className="muted small" style={{ marginTop: 0 }}>{posterLabel(l.poster_role)}</p>}
                  {/* Only what the seller chose to publish reaches this page —
                      the server withholds the rest rather than the interface
                      hiding it, and the default is to publish nothing. */}
                  {l.contact_phone && <p><a href={`tel:${l.contact_phone}`}>{l.contact_phone}</a></p>}
                  {l.contact_email && <p><a href={`mailto:${l.contact_email}`}>{l.contact_email}</a></p>}
                  {!l.contact_phone && !l.contact_email && (
                    <p className="muted">Use the form to get in touch.</p>
                  )}
                </div>
                <SaleEnquiry listingId={l.id} contactName={l.contact_name} />
              </>
            )}
            {/*
              * Under the contact block, where somebody who has just tried to
              * reach the seller is standing. A property at this price attracts
              * frauds a moderator cannot see in a browser — a plot sold twice,
              * a title that is not the seller's, photographs lifted from
              * another advert — and the person who visited the gate is the only
              * one who finds that out. Nothing is taken down by a report; it
              * goes to a human, which at this price point is the safeguard
              * rather than the shortcoming.
              */}
            <div className="dsc-contact" style={{ marginTop: 12 }}>
              <ReportListing targetType="sale_listing" targetId={l.id} what="property" />
            </div>
          </aside>
        </div>

        {view.data!.similar.length > 0 && (
          <section className="dsc-section dsc-similar">
            <h2>Similar properties</h2>
            <div className="dsc-grid">
              {view.data!.similar.map((s) => <SaleCard key={s.id} listing={s} />)}
            </div>
          </section>
        )}
      </div>

      <SiteFooter />
    </>
  );
}
